import React, { useState, useEffect } from 'react';
import {
  Briefcase, Plus, Edit2, Trash2, X, Save, Search,
  AlertCircle, CheckCircle, ChevronDown, ChevronUp, FileText
} from 'lucide-react';
import api from '../../services/apiService';

const OEMWorkScopes = () => {
  const [workScopes, setWorkScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingScope, setEditingScope] = useState(null);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedScope, setExpandedScope] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    is_hazardous: false,
    requires_supervision: false,
    order: 0
  });

  // Fetch work scopes
  const fetchWorkScopes = async () => {
    setLoading(true);
    try {
      const response = await api.get('/oem/work-scopes/');
      setWorkScopes(response.data || []);
    } catch (err) {
      console.error('Error fetching work scopes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkScopes();
  }, []);

  // Open modal for create/edit
  const openModal = (scope = null) => {
    if (scope) {
      setEditingScope(scope);
      setFormData({
        name: scope.name || '',
        code: scope.code || '',
        description: scope.description || '',
        is_hazardous: scope.is_hazardous || false,
        requires_supervision: scope.requires_supervision || false,
        order: scope.order || 0
      });
    } else {
      setEditingScope(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        is_hazardous: false,
        requires_supervision: false,
        order: 0
      });
    }
    setShowModal(true);
  };

  // Save work scope
  const handleSave = async () => {
    if (!formData.name.trim()) {
      alert('Name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingScope) {
        await api.patch(`/oem/work-scopes/${editingScope.id}/`, formData);
      } else {
        await api.post('/oem/work-scopes/', formData);
      }
      setShowModal(false);
      fetchWorkScopes();
    } catch (err) {
      console.error('Error saving work scope:', err);
      alert(err.response?.data?.detail || err.response?.data?.name?.[0] || 'Failed to save work scope');
    } finally {
      setSaving(false);
    }
  };

  // Delete work scope
  const handleDelete = async (scope) => {
    if (!window.confirm(`Are you sure you want to delete "${scope.name}"?`)) return;

    try {
      await api.delete(`/oem/work-scopes/${scope.id}/`);
      fetchWorkScopes();
    } catch (err) {
      console.error('Error deleting work scope:', err);
      alert('Failed to delete work scope');
    }
  };

  // Filter work scopes
  const filteredScopes = workScopes.filter(ws => {
    const term = searchTerm.toLowerCase();
    return (
      ws.name.toLowerCase().includes(term) ||
      (ws.code || '').toLowerCase().includes(term) ||
      (ws.description || '').toLowerCase().includes(term)
    );
  });

  if (loading) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="bg-white rounded-2xl p-10 flex items-center justify-center shadow-sm border border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-slate-600 text-sm font-medium">Loading work scopes...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
            <Briefcase className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-lg font-black text-slate-900 uppercase tracking-tight">Work Scopes</h1>
            <p className="text-[10px] text-slate-500 font-medium">Define types of work for your OEM projects</p>
          </div>
        </div>
        <button
          onClick={() => openModal()}
          className="h-9 px-4 bg-orange-500 text-white rounded-xl hover:bg-orange-600 flex items-center gap-1.5 font-black uppercase tracking-wide text-[10px] transition-colors shadow-lg shadow-orange-500/20"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Work Scope
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search work scopes..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-300"
          />
        </div>
      </div>

      {/* Work Scopes List */}
      <div className="space-y-3">
        {filteredScopes.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-10 text-center">
            <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-3">
              <Briefcase className="h-6 w-6 text-slate-400" />
            </div>
            <p className="text-sm font-black text-slate-900">No work scopes found</p>
            <p className="text-xs text-slate-500 mt-1">
              {searchTerm ? 'Try adjusting your search' : 'Create your first work scope to get started'}
            </p>
          </div>
        ) : (
          filteredScopes.map(scope => (
            <div
              key={scope.id}
              className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden"
            >
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                    scope.is_hazardous ? 'bg-amber-100' : 'bg-emerald-100'
                  }`}>
                    <FileText className={`h-5 w-5 ${
                      scope.is_hazardous ? 'text-amber-600' : 'text-emerald-600'
                    }`} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-sm font-black text-slate-900">{scope.name}</h3>
                      {scope.code && (
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-[9px] font-black rounded-lg uppercase">
                          {scope.code}
                        </span>
                      )}
                      {scope.is_hazardous && (
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-black rounded-lg uppercase">
                          Hazardous
                        </span>
                      )}
                      {scope.requires_supervision && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[9px] font-black rounded-lg uppercase">
                          Supervision
                        </span>
                      )}
                    </div>
                    {scope.description && (
                      <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{scope.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {scope.categories?.length > 0 && (
                    <button
                      onClick={() => setExpandedScope(expandedScope === scope.id ? null : scope.id)}
                      className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                    >
                      {expandedScope === scope.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  )}
                  <button
                    onClick={() => openModal(scope)}
                    className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-colors"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(scope)}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Categories (if expanded) */}
              {expandedScope === scope.id && scope.categories?.length > 0 && (
                <div className="px-4 pb-4 pt-0 border-t border-slate-100">
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-wide mb-2 mt-3">Categories</p>
                  <div className="space-y-1">
                    {scope.categories.map(cat => (
                      <div key={cat.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                        <span className="text-sm text-slate-700">{cat.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
            <div className="p-5 border-b border-slate-100">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Briefcase className="h-4 w-4 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-slate-900 uppercase tracking-tight">
                      {editingScope ? 'Edit Work Scope' : 'New Work Scope'}
                    </h3>
                  </div>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wider mb-1.5">
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., Annual Maintenance"
                  className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wider mb-1.5">
                  Code (optional)
                </label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g., AM"
                  maxLength={20}
                  className="w-full h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                />
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wider mb-1.5">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Brief description of this work scope..."
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm resize-none"
                />
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_hazardous}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_hazardous: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-xs font-bold text-slate-700">Hazardous Work</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.requires_supervision}
                    onChange={(e) => setFormData(prev => ({ ...prev, requires_supervision: e.target.checked }))}
                    className="w-4 h-4 rounded border-slate-300 text-orange-500 focus:ring-orange-500"
                  />
                  <span className="text-xs font-bold text-slate-700">Requires Supervision</span>
                </label>
              </div>

              <div>
                <label className="block text-[10px] font-black text-slate-700 uppercase tracking-wider mb-1.5">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData(prev => ({ ...prev, order: parseInt(e.target.value) || 0 }))}
                  min={0}
                  className="w-24 h-10 px-3 border border-slate-200 rounded-xl bg-white focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 text-sm"
                />
              </div>
            </div>

            <div className="p-5 border-t border-slate-100 flex items-center justify-end gap-2">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-slate-600 font-black text-[10px] uppercase tracking-wide hover:bg-slate-100 rounded-lg transition-colors"
                disabled={saving}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.name.trim()}
                className="px-5 py-2 bg-orange-500 text-white font-black text-[10px] uppercase tracking-wide rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-3.5 w-3.5" />
                    {editingScope ? 'Update' : 'Create'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OEMWorkScopes;
